import { update as updateIntercom } from "@intercom/messenger-js-sdk";

const STORAGE_KEY = "vello_attribution";
const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content"
];
// Source name -> domain; a trailing dot matches any TLD (google.com, google.fi)
const SEARCH_ENGINES: Record<string, string> = {
  google: "google.",
  bing: "bing.com",
  duckduckgo: "duckduckgo.com",
  yahoo: "yahoo.",
  ecosia: "ecosia.org",
  yandex: "yandex."
};
const SOCIAL_DOMAINS = [
  "facebook.com",
  "instagram.com",
  "linkedin.com",
  "twitter.com",
  "x.com",
  "t.co",
  "youtube.com",
  "tiktok.com"
];

interface Attribution {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  gclid: string | null;
  fbclid: string | null;
  msclkid: string | null;
  li_fat_id: string | null;
  referrer: string | null;
  landing_page: string;
  first_touch_at: string;
  attribution_channel: string;
  attribution_source: string;
}

type Touchpoint = Omit<
  Attribution,
  "attribution_channel" | "attribution_source"
>;

let registrationTracked = false;

// Match whole domain labels, so "t.co" doesn't match microsoft.com
const hostMatches = (host: string, domain: string): boolean =>
  domain.endsWith(".")
    ? host.startsWith(domain) || host.includes(`.${domain}`)
    : host === domain || host.endsWith(`.${domain}`);

const getReferrerHost = (referrer: string | null): string | null => {
  if (!referrer) return null;
  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch (error) {
    return null;
  }
};

const getSearchEngine = (host: string): string | undefined =>
  Object.keys(SEARCH_ENGINES).find(name =>
    hostMatches(host, SEARCH_ENGINES[name])
  );

const classifyChannel = (touchpoint: Touchpoint): string => {
  const medium = (touchpoint.utm_medium || "").toLowerCase();
  if (["cpc", "ppc", "paidsearch"].includes(medium)) return "paid_search";
  if (["paid_social", "paidsocial"].includes(medium)) return "paid_social";
  if (medium === "email") return "email";
  if (medium === "organic") return "organic_search";
  if (medium === "social") return "organic_social";
  if (medium === "referral") return "referral";
  // Ad clicks without UTM tags
  if (touchpoint.gclid || touchpoint.msclkid) return "paid_search";
  if (touchpoint.fbclid || touchpoint.li_fat_id) return "paid_social";

  const host = getReferrerHost(touchpoint.referrer);
  if (!host) return "direct";
  if (getSearchEngine(host)) return "organic_search";
  if (SOCIAL_DOMAINS.some(domain => hostMatches(host, domain)))
    return "organic_social";
  if (hostMatches(host, "vello.fi")) return "internal";
  return "referral";
};

const deriveSource = (touchpoint: Touchpoint): string => {
  if (touchpoint.utm_source) return touchpoint.utm_source;
  const host = getReferrerHost(touchpoint.referrer);
  if (!host) return "(direct)";
  return getSearchEngine(host) || host.replace(/^www\./, "");
};

/**
  Store first-touch marketing attribution from the current (landing) URL.
  Write-once: an existing entry is never overwritten, so the first visit wins.
*/
export const captureAttribution = (): void => {
  try {
    if (localStorage.getItem(STORAGE_KEY)) return;

    const params = new URLSearchParams(window.location.search);
    // Only UTM tags go into landing_page: other query values can carry
    // personal data (emails, tokens) that must not end up in Intercom
    const landingParams = new URLSearchParams();
    UTM_PARAMS.forEach(key => {
      const value = params.get(key);
      if (value) landingParams.set(key, value);
    });
    const landingQuery = landingParams.toString();

    const touchpoint: Touchpoint = {
      utm_source: params.get("utm_source") || null,
      utm_medium: params.get("utm_medium") || null,
      utm_campaign: params.get("utm_campaign") || null,
      utm_term: params.get("utm_term") || null,
      utm_content: params.get("utm_content") || null,
      gclid: params.get("gclid") || null,
      fbclid: params.get("fbclid") || null,
      msclkid: params.get("msclkid") || null,
      li_fat_id: params.get("li_fat_id") || null,
      // Links from www.vello.fi pass the visitor's original referrer here;
      // an empty value means they reached www.vello.fi directly
      referrer: (params.get("_orig_referrer") ?? document.referrer) || null,
      landing_page:
        window.location.pathname + (landingQuery ? `?${landingQuery}` : ""),
      first_touch_at: new Date().toISOString()
    };
    const attribution: Attribution = {
      ...touchpoint,
      attribution_channel: classifyChannel(touchpoint),
      attribution_source: deriveSource(touchpoint)
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch (error) {
    // Storage blocked (private mode, disabled cookies): attribution is best-effort
  }
};

// Intercom text attributes hold at most 255 characters
const toIntercomText = (value?: string | null): string | null =>
  value ? value.slice(0, 255) : null;

const sendAttributionToIntercom = (): void => {
  const attribution: Partial<Attribution> = JSON.parse(
    localStorage.getItem(STORAGE_KEY) || "{}"
  );
  if (!Object.keys(attribution).length) return;

  updateIntercom({
    attribution_channel: toIntercomText(attribution.attribution_channel),
    attribution_source: toIntercomText(attribution.attribution_source),
    // Intercom date attributes (*_at) take a Unix timestamp in seconds
    first_touch_at:
      Math.floor(Date.parse(attribution.first_touch_at || "") / 1000) || null,
    utm_source: toIntercomText(attribution.utm_source),
    utm_medium: toIntercomText(attribution.utm_medium),
    utm_campaign: toIntercomText(attribution.utm_campaign),
    utm_term: toIntercomText(attribution.utm_term),
    utm_content: toIntercomText(attribution.utm_content),
    landing_page: toIntercomText(attribution.landing_page),
    referrer: toIntercomText(attribution.referrer)
  });
};

const toHex = (bytes: Uint8Array): string =>
  Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");

const createEventId = (): string => {
  const webCrypto = window.crypto as Crypto & { randomUUID?: () => string };
  if (webCrypto.randomUUID) return webCrypto.randomUUID();

  // UUID v4 for browsers without randomUUID (Safari < 15.4, plain-HTTP hosts)
  const bytes = webCrypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = toHex(bytes);
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20)
  ].join("-");
};

// Only the hash may reach the dataLayer, never the raw email.
// Web Crypto is unavailable on plain-HTTP hosts, so the hash is optional.
const hashEmail = async (email: string): Promise<string | null> => {
  if (!window.crypto?.subtle) return null;
  const digest = await window.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(email.trim().toLowerCase())
  );
  return toHex(new Uint8Array(digest));
};

/**
  Report a completed registration: first-touch attribution to Intercom and the
  registration_complete conversion to GTM. Runs at most once per page load and
  never rejects, so tracking can't block the redirect that follows.
  @param email - email the account was registered with
*/
export const trackRegistrationComplete = async (
  email: string
): Promise<void> => {
  if (registrationTracked) return;
  registrationTracked = true;

  try {
    sendAttributionToIntercom();
  } catch (error) {
    // Unreadable storage: skip Intercom but still report the conversion
  }

  try {
    const emailSha256 = await hashEmail(email).catch(() => null);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "registration_complete",
      // Dedup key if the same conversion is later also sent server-side
      event_id: createEventId(),
      ...(emailSha256 && { email_sha256: emailSha256 })
    });
  } catch (error) {
    // Best-effort: the account is created either way
  }
};
